import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useRealtimeRefetch } from '../hooks/useRealtimeRefetch';
import apiService, { RatedMovieResponse, MovieStats, GenreStat, RatingStat } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import TutorialModal from '../components/TutorialModal';
import styled from 'styled-components';

// --- Constants ---

const CHART_COLORS = [
  '#6366f1', '#06b6d4', '#10b981', '#f59e0b', '#f43f5e',
  '#8b5cf6', '#0ea5e9', '#34d399', '#fb923c', '#e879f9',
  '#a78bfa', '#2dd4bf', '#4ade80', '#fb7185', '#fbbf24',
  '#818cf8', '#60a5fa', '#f87171', '#a3e635',
];

const RATING_BAR_COLORS = ['#e53e3e', '#8B4513', '#CD7F32', '#A8A9AD', '#FFD700'];
const BAR_W = 28;
const BAR_GAP = 12;
const BAR_SVG_W = 5 * BAR_W + 4 * BAR_GAP + 32;
const BAR_SVG_H = 170;
const BAR_CHART_TOP = 22;
const BAR_CHART_BOT = 26;
const BAR_CHART_H = BAR_SVG_H - BAR_CHART_TOP - BAR_CHART_BOT;
const BAR_LEFT = (BAR_SVG_W - (5 * BAR_W + 4 * BAR_GAP)) / 2;

// --- Styled components ---

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const StatsCard = styled.div`
  background: var(--color-surface);
  padding: 1.75rem 2rem;
  border-radius: 12px;
  box-shadow: var(--shadow-card);
  text-align: center;
`;

const StatsNumber = styled.div`
  font-size: 3rem;
  font-weight: bold;
  color: var(--color-accent);
  line-height: 1;
  margin-bottom: 0.4rem;
`;

const StatsLabel = styled.div`
  font-size: 1.05rem;
  color: var(--color-text-secondary);
`;

const StatsSubLabel = styled.div`
  font-size: 0.8rem;
  color: var(--color-text-muted);
  margin-top: 0.3rem;
`;

const MiddleRow = styled.div`
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const WatchedCard = styled.div`
  background: var(--color-surface);
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: var(--shadow-card);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const StatGroup = styled.div``;

const StatGroupLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--color-text-muted);
  margin-bottom: 0.3rem;
`;

const StatGroupValue = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text);
`;

const StatGroupCount = styled.span`
  font-size: 0.85rem;
  color: var(--color-text-muted);
  font-weight: 400;
`;

const ChartCard = styled.div`
  background: var(--color-surface);
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: var(--shadow-card);
  display: flex;
  gap: 2rem;
  align-items: center;
`;

const LegendGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.3rem 1.25rem;
  overflow-y: auto;
  max-height: 220px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
`;

const LegendDot = styled.div<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${p => p.$color};
  flex-shrink: 0;
`;

const LegendName = styled.span`
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LegendCount = styled.span`
  color: var(--color-text-muted);
  flex-shrink: 0;
  margin-left: auto;
`;

const Section = styled.div`
  background: var(--color-surface);
  padding: 2rem;
  border-radius: 12px;
  box-shadow: var(--shadow-card);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  color: var(--color-text);
  margin-bottom: 1.5rem;
  font-size: 1.3rem;
`;

const MovieGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
`;

const MovieCard = styled.div`
  background: var(--color-surface-subtle);
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }
`;

const MovieTitle = styled.h3`
  color: var(--color-text);
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const MovieRating = styled.div`
  font-weight: bold;
  font-size: 1.1rem;
  color: var(--color-text);
`;

const MovieDate = styled.div`
  color: var(--color-text-secondary);
  font-size: 0.8rem;
  margin-top: 0.4rem;
`;

const ChartWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const Tooltip = styled.div<{ $x: number; $y: number }>`
  position: absolute;
  left: ${p => p.$x + 14}px;
  top: ${p => p.$y - 12}px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  pointer-events: none;
  white-space: nowrap;
  z-index: 50;
  font-size: 0.85rem;
`;

const TooltipName = styled.div`
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 0.2rem;
`;

const TooltipRow = styled.div`
  color: var(--color-text-secondary);
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--color-text-secondary);
`;

const BarChartWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  border-left: 1px solid var(--color-border-subtle);
  padding-left: 1.5rem;
`;

const BarChartTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--color-text-muted);
  align-self: flex-start;
`;

// --- Helpers ---

function getStarColor(rating: number): string {
  if (rating >= 5) return '#FFD700';
  if (rating >= 4) return '#A8A9AD';
  if (rating >= 3) return '#CD7F32';
  if (rating >= 2) return '#8B4513';
  return '#e53e3e';
}

interface PieSlice {
  path: string;
  color: string;
  lx: number;
  ly: number;
  pct: number;
  genre: GenreStat;
  index: number;
}

function buildPieSlices(data: GenreStat[], cx: number, cy: number, r: number): PieSlice[] {
  const total = data.reduce((s, d) => s + d.count, 0);
  if (total === 0) return [];

  let angle = -Math.PI / 2;
  return data.map((genre, i) => {
    const sweep = (genre.count / total) * 2 * Math.PI;
    const start = angle;
    angle += sweep;
    const end = angle;
    const mid = start + sweep / 2;

    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy + r * Math.sin(end);
    const lx = cx + r * 0.62 * Math.cos(mid);
    const ly = cy + r * 0.62 * Math.sin(mid);

    return {
      path: `M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${sweep > Math.PI ? 1 : 0} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`,
      color: CHART_COLORS[i % CHART_COLORS.length],
      lx, ly,
      pct: genre.count / total,
      genre,
      index: i,
    };
  });
}

function RatingBarChart({ data }: { data: RatingStat[] }) {
  const maxCount = Math.max(...data.map(d => d.count), 1);
  return (
    <svg width={BAR_SVG_W} height={BAR_SVG_H}>
      {data.map((d, i) => {
        const barH = Math.max((d.count / maxCount) * BAR_CHART_H, d.count > 0 ? 3 : 0);
        const x = BAR_LEFT + i * (BAR_W + BAR_GAP);
        const y = BAR_CHART_TOP + BAR_CHART_H - barH;
        return (
          <g key={d.rating}>
            <rect x={x} y={y} width={BAR_W} height={barH} fill={RATING_BAR_COLORS[i]} rx={3} />
            {d.count > 0 && (
              <text
                x={x + BAR_W / 2} y={y - 4}
                textAnchor="middle" fontSize={11}
                fill="var(--color-text-secondary)"
                style={{ pointerEvents: 'none' }}
              >
                {d.count}
              </text>
            )}
            <text
              x={x + BAR_W / 2} y={BAR_SVG_H - 6}
              textAnchor="middle" fontSize={12}
              fill={RATING_BAR_COLORS[i]} fontWeight="700"
              style={{ pointerEvents: 'none' }}
            >
              {d.rating}★
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// --- Component ---

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [recentRatings, setRecentRatings] = useState<RatedMovieResponse[]>([]);
  const [stats, setStats] = useState<MovieStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    if (user?.id) {
      const key = `tutorial_seen_${user.id}`;
      if (!localStorage.getItem(key)) {
        setShowTutorial(true);
      }
    }
  }, [user?.id]);

  const handleTutorialClose = () => {
    if (user?.id) {
      localStorage.setItem(`tutorial_seen_${user.id}`, '1');
    }
    setShowTutorial(false);
  };

  const loadDashboard = useCallback(() => {
    Promise.all([
      apiService.getRatedMovies(1, 5, 'date_rated', 'desc'),
      apiService.getMovieStats(),
    ])
      .then(([ratingsRes, statsRes]) => {
        setRecentRatings(ratingsRes.items);
        setStats(statsRes);
      })
      .catch(err => console.error('Dashboard fetch failed:', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  useRealtimeRefetch(['rated_movies'], loadDashboard);

  const topGenres = useMemo((): GenreStat[] => {
    const dist = stats?.genre_distribution ?? [];
    if (dist.length <= 10) return dist;
    const top = dist.slice(0, 10);
    const rest = dist.slice(10);
    const restCount = rest.reduce((s, g) => s + g.count, 0);
    const restTotal = rest.reduce((s, g) => s + g.avg_rating * g.count, 0);
    top.push({
      genre_id: -1,
      genre_name: 'Other',
      count: restCount,
      avg_rating: restCount > 0 ? Math.round((restTotal / restCount) * 10) / 10 : 0,
    });
    return top;
  }, [stats?.genre_distribution]);

  const pieSlices = useMemo(
    () => buildPieSlices(topGenres, 110, 110, 95),
    [topGenres]
  );

  const [hoveredSlice, setHoveredSlice] = useState<PieSlice | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const handleChartMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  if (loading) return <LoadingSpinner>Loading dashboard...</LoadingSpinner>;

  const formatDate = (s: string) => new Date(s).toLocaleDateString();

  return (
    <div>
      {showTutorial && <TutorialModal onClose={handleTutorialClose} />}
      {/* Stats row */}
      <StatsRow>
        <StatsCard>
          <StatsNumber>{stats?.total_ratings ?? 0}</StatsNumber>
          <StatsLabel>Movies Rated</StatsLabel>
        </StatsCard>
        <StatsCard>
          <StatsNumber>{stats?.average_rating ?? 0}</StatsNumber>
          <StatsLabel>Average Rating</StatsLabel>
        </StatsCard>
        <StatsCard>
          <StatsNumber>{stats?.provider_suggestions ?? '—'}</StatsNumber>
          <StatsLabel>Recommended on Your Platforms</StatsLabel>
          {stats && stats.total_suggestions !== stats.provider_suggestions && (
            <StatsSubLabel>{stats.total_suggestions} total</StatsSubLabel>
          )}
        </StatsCard>
      </StatsRow>

      {/* Middle row: watched stats + genre chart */}
      {stats && (stats.top_director || stats.top_actor || stats.top_franchise || stats.genre_distribution.length > 0) && (
        <MiddleRow>
          <WatchedCard>
            {stats.top_director && (
              <StatGroup>
                <StatGroupLabel>Most Watched Director</StatGroupLabel>
                <StatGroupValue>
                  {stats.top_director.name}{' '}
                  <StatGroupCount>({stats.top_director.count})</StatGroupCount>
                </StatGroupValue>
              </StatGroup>
            )}
            {stats.top_actor && (
              <StatGroup>
                <StatGroupLabel>Most Watched Actor / Actress</StatGroupLabel>
                <StatGroupValue>
                  {stats.top_actor.name}{' '}
                  <StatGroupCount>({stats.top_actor.count})</StatGroupCount>
                </StatGroupValue>
              </StatGroup>
            )}
            {stats.top_franchise && (
              <StatGroup>
                <StatGroupLabel>Most Watched Series</StatGroupLabel>
                <StatGroupValue>
                  {stats.top_franchise.name}{' '}
                  <StatGroupCount>({stats.top_franchise.count})</StatGroupCount>
                </StatGroupValue>
              </StatGroup>
            )}
          </WatchedCard>

          <ChartCard>
            {pieSlices.length > 0 ? (
              <>
                <ChartWrapper>
                  <svg
                    width={220} height={220} viewBox="0 0 220 220"
                    onMouseMove={handleChartMouseMove}
                    onMouseLeave={() => setHoveredSlice(null)}
                  >
                    {pieSlices.map(slice => (
                      <g key={slice.genre.genre_id}>
                        <path
                          d={slice.path}
                          fill={slice.color}
                          stroke="var(--color-surface)"
                          strokeWidth={2}
                          opacity={hoveredSlice && hoveredSlice.genre.genre_id !== slice.genre.genre_id ? 0.65 : 1}
                          style={{ cursor: 'pointer', transition: 'opacity 0.15s' }}
                          onMouseEnter={() => setHoveredSlice(slice)}
                        />
                        {slice.pct > 0.06 && (
                          <text
                            x={slice.lx}
                            y={slice.ly}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize={10}
                            fontWeight="700"
                            fill="white"
                            style={{ pointerEvents: 'none' }}
                          >
                            {slice.genre.avg_rating}★
                          </text>
                        )}
                      </g>
                    ))}
                  </svg>
                  {hoveredSlice && (
                    <Tooltip $x={tooltipPos.x} $y={tooltipPos.y}>
                      <TooltipName>{hoveredSlice.genre.genre_name}</TooltipName>
                      <TooltipRow>{hoveredSlice.genre.count} {hoveredSlice.genre.count === 1 ? 'movie' : 'movies'}</TooltipRow>
                      <TooltipRow>{hoveredSlice.genre.avg_rating}★ avg rating</TooltipRow>
                    </Tooltip>
                  )}
                </ChartWrapper>
                <LegendGrid>
                  {pieSlices.map(slice => (
                    <LegendItem key={slice.genre.genre_id}>
                      <LegendDot $color={slice.color} />
                      <LegendName>{slice.genre.genre_name}</LegendName>
                      <LegendCount>{slice.genre.count}</LegendCount>
                    </LegendItem>
                  ))}
                </LegendGrid>
                {(stats?.rating_distribution ?? []).length > 0 && (
                  <BarChartWrapper>
                    <BarChartTitle>By Rating</BarChartTitle>
                    <RatingBarChart data={stats!.rating_distribution} />
                  </BarChartWrapper>
                )}
              </>
            ) : (
              <span style={{ color: 'var(--color-text-muted)' }}>
                Rate some movies to see genre breakdown.
              </span>
            )}
          </ChartCard>
        </MiddleRow>
      )}

      {/* Recent ratings */}
      <Section>
        <SectionTitle>Recent Ratings</SectionTitle>
        {recentRatings.length > 0 ? (
          <MovieGrid>
            {recentRatings.map(movie => (
              <MovieCard key={movie.id}>
                <MovieTitle>{movie.title}</MovieTitle>
                <MovieRating>
                  <span style={{ color: getStarColor(movie.user_rating), fontSize: '1.3rem' }}>★</span>
                  {' '}{movie.user_rating}/5
                </MovieRating>
                <MovieDate>{formatDate(movie.date_rated)}</MovieDate>
              </MovieCard>
            ))}
          </MovieGrid>
        ) : (
          <p style={{ color: 'var(--color-text-secondary)' }}>No movies rated yet. Start by searching for movies!</p>
        )}
      </Section>
    </div>
  );
};

export default DashboardPage;
